/**
 * @module Carousel
 */

import Swiper from 'swiper';
import Component from '../Component';
import { rMediaMatch } from '../../../scripts/regex';
import '../../../images/sprite/arrow.svg';
import '../../../images/sprite/arrow_lg.svg';
import 'swiper/dist/css/swiper.css';
import './swiper.css';
import './styles.css';
import '../../../styles/preloader.css';

/**
 * Карусель.
 *
 * Использует плагин Swiper.
 * Для прямого доступа к функционалу плагина используется поле instance.
 *
 * Если на разных брейкпоинтах нужны разные элементы навигации, то в шаблоне подключаются все сразу,
 * и прописывается сокрытие через медиазапросы в стилях. Дополнительная логика для этого не требуется.
 *
 * @example <caption>Переопределение "лёгких" свойств</caption>
 * // К "лёгким" свойствам относится, например, число слайдов.
 * carousel.init({}, {
 *     slidesPerView: 5,
 *     breakpoints: {
 *         [breakpoints.tablet - 1]: {
 *             slidesPerView: 'auto'
 *         },
 *         [breakpoints.tabletLg - 1]: {
 *             slidesPerView: 3
 *         }
 *     },
 * });
 *
 * @example <caption>Переопределение "тяжёлых" свойств</caption>
 * // "Тяжёлые" свойства требуют повторной инициализации. Например, изменение ориентации.
 * carousel.init({
 *      reinitBreakpoints: [media('tablet')],
 *      reinitOptions: {
 *          default: {
 *              direction: 'vertical',
 *          },
 *          [breakpoints.tablet - 1]: {
 *              direction: 'horizontal',
 *          },
 *      },
 * });
 *
 * @example <caption>Динамическое разрушение карусели</caption>
 * carousel.init({
 *      destroyMode: 'next',
 *      destroyBreakpoint: media('desktop'),
 * });
 *
 * @example <caption>Использование Controller</caption>
 * carousel1.init();
 * carousel2.init();
 * carousel1.initController(carousel2);
 * carousel2.initController(carousel1);
 *
 * @see Swiper: {@link http://idangero.us/swiper/api/}
 *
 * @todo Карусели периодически разваливаются на ресайзе с ошибкой в консоли.
 * @todo В loop-режиме периодически не отрабатывает transition.
 * @todo В loop-режиме периодически клик по слайду прокручивает карусель назад.
 * @todo Нет возможности задать центральный режим с отображением части прилегающих слайдов через slidesPerView: auto.
 * @todo Реализованный механизм фильтрации не работает в loop-режиме.
 * @todo Смена значения effect ломает карусели, т.е. сейчас работает только 'slide'.
 */
export default class Carousel extends Component {
    static className = 'carousel';

    /**
     * Проверяет нужно ли на данном разрешении убирать карусель.
     *
     * @param {Object} breakpoint Объект MatchMedia.
     * @param {string} mode Режим ('prev' - убирать карусель на меньших разрешениях, 'next' - на больших).
     * @return {boolean} Результат проверки.
     */
    static needsDestroy(breakpoint, mode) {
        return (mode === 'prev' && breakpoint.matches) || (mode === 'next' && !breakpoint.matches);
    }

    initElements() {
        this.elements = {
            $navPrev: $('.swiper-button-prev', this.$block),
            $navNext: $('.swiper-button-next', this.$block),
            $pagination: $('.swiper-pagination', this.$block),
            $scrollbar: $('.swiper-scrollbar', this.$block),
        };
    }

    setTemplateProperties() {
        /**
         * Кнопки навигации.
         * @type {boolean}
         */
        this.navigation = Boolean(this.elements.$navPrev.length);

        /**
         * Пагинация.
         * @type {string}
         */
        this.pagination = this.$block.attr('data-pagination');

        /**
         * Скроллбар.
         * @type {boolean}
         */
        this.scrollbar = Boolean(this.elements.$scrollbar.length);
    }

    setProperties({
        vertical = false,
        speed = 300,
        autoHeight = false,
        loop = true,
        effect = 'slide',
        dynamicBullets = false,
        autoplay = false,
        freeMode = false,
        centerMode = false,
        lazy = false,
        spaceBetween = 0,
        destroyBreakpoint,
        destroyMode = 'prev',
        reinitBreakpoints,
        reinitOptions,
    }) {
        this.options = {
            autoHeight,
            effect,
            freeMode,
            loop,
            speed: Number(speed),
            spaceBetween: Number(spaceBetween),
            centeredSlides: centerMode,
            direction: vertical ? 'vertical' : 'horizontal',
            /* Переключение слайдов через стрелки клавиатуры для каруселей в области видимости. */
            keyboard: true,
            /* Переход к слайду по клику. */
            slideToClickedSlide: true,
            /* Курсор типа 'grab'. */
            grabCursor: true,
            /* Отключение работы карусели при числе слайдов, меньшем slidesPerView, и добавление lock-классов элементам навигации. */
            watchOverflow: true,
            /* Дополнительные клонированные слайды для улучшения работы циклов (значение определено эмпирическим путём). */
            loopAdditionalSlides: 10,
            /* a11y прописывается самостоятельно, т.к. Swiper местами добавляет лишнее (например, role="button" для пагинации). */
            a11y: false,
        };

        /**
         * Динамические буллеты.
         * @type {number}
         */
        this.dynamicBullets = dynamicBullets;

        if (autoplay) {
            this.options.autoplay = {
                delay: Number(autoplay),
            };
        }

        if (lazy) {
            this.options.lazy = {
                loadPrevNext: true,
                loadOnTransitionStart: true,
            };

            this.options.preloadImages = false;
        }

        if (destroyBreakpoint) {
            /**
             * Режим разрушения на адаптиве.
             * @type {'prev' | 'next'}
             */
            this.destroyMode = destroyMode;

            /**
             * Точка разрушения.
             * @type {MediaQueryList}
             */
            this.destroyBreakpoint = destroyBreakpoint;
        }

        if (reinitBreakpoints) {
            /**
             * Массив из точек реинициализации.
             * @type {MediaQueryList[]}
             */
            this.reinitBreakpoints = reinitBreakpoints;

            /**
             * Настройки для реинициализации (формат аналогичный breakpoints).
             * @type {Object}
             */
            this.reinitOptions = reinitOptions;
        }
    }

    initPlugin(pluginOptions) {
        if (this.navigation) {
            this.options.navigation = {
                prevEl: this.elements.$navPrev,
                nextEl: this.elements.$navNext,
            };
        }

        if (this.pagination) {
            /* Для всех 3-ёх видов пагинации можно задать кастомный рендер через функцию. Для совсем специфичной пагинации задаётся тип 'custom' и прописывается renderCustom. */

            this.options.pagination = {
                el: this.elements.$pagination,
                type: this.pagination,
            };

            if (this.pagination === 'bullets') {
                this.options.pagination.bulletElement = 'li';
                this.options.pagination.clickable = true;

                /* Здесь видоизменяется разметка буллетов - в каждый элемент списка пагинации вкладывается кнопка. Такой подход является более семантически верным и проходит a11y тесты. */

                this.options.pagination.renderBullet = (index, className) =>
                    `<li class="${className}"><button class="carousel__pagination-bullet" type="button">Перейти к слайду ${index +
                        1}</button></li>`;

                if (this.dynamicBullets) {
                    this.options.pagination.dynamicBullets = true;
                    this.options.pagination.dynamicMainBullets = Number(this.dynamicBullets);
                }
            }
        }

        if (this.scrollbar) {
            this.options.scrollbar = {
                el: this.elements.$scrollbar,
                draggable: true,
                /* В free mode не нужна привязка к слайдам, в остальных случаях включаем */
                snapOnRelease: !this.freeMode,
            };
        }

        Object.assign(this.options, pluginOptions);

        let needsDestroy = false;

        if (this.destroyBreakpoint) {
            this.destroyBreakpoint.addListener(() => {
                this.destroy(this.destroyBreakpoint, this.destroyMode);
            });

            needsDestroy = Carousel.needsDestroy(this.destroyBreakpoint, this.destroyMode);
        }

        if (this.reinitBreakpoints) {
            let actualBreakpoint;
            let breakpointValue;

            this.reinitBreakpoints.forEach(breakpoint => {
                breakpoint.addListener(() => {
                    this.reinit(breakpoint);
                });

                if (breakpoint.matches) {
                    if (!actualBreakpoint || Number(breakpoint.media.match(rMediaMatch)[1]) < breakpointValue) {
                        actualBreakpoint = breakpoint;
                        breakpointValue = Number(breakpoint.media.match(rMediaMatch)[1]);
                    }
                }
            });

            if (actualBreakpoint) {
                const newOptions = this.reinitOptions[breakpointValue];

                Object.assign(this.options, newOptions);
            }
        }

        /* Если on отсутствует, то его нужно прописать. */
        if (!this.options.on) {
            this.options.on = {};
        }

        /* Убирает прелоадер после инициализации. */
        this.options.on.init = () => {
            this.$block.removeClass('is-loading');
        };

        /* Если на текущем разрешении карусели нет, то она не инициализируется,
        так что необходимо убрать прелоадер вне инита. */
        if (needsDestroy) {
            this.$block.removeClass('is-loading');
        }

        if (!needsDestroy) {
            this.instance = new Swiper(this.$block, this.options);
        }
    }

    /**
     * Инициализирует/убирает карусель в зависимости от разрешения.
     *
     * @param {Object} breakpoint Объект MatchMedia.
     * @param {string} mode Режим ('prev' - убирать карусель на меньших разрешениях, 'next' - на больших).
     */
    destroy(breakpoint, mode) {
        if (Carousel.needsDestroy(breakpoint, mode)) {
            this.instance.destroy();
        } else {
            this.instance = new Swiper(this.$block, this.options);
        }
    }

    /**
     * Пересоздаёт карусель с новыми настройками
     * Используется для изменения "тяжёлых" настроек на адаптиве.
     *
     * @param {Object} breakpoint Объект MatchMedia.
     */
    reinit(breakpoint) {
        let breakpointValue;
        let newOptions;

        if (breakpoint.matches) {
            breakpointValue = breakpoint.media.match(rMediaMatch)[1];
            newOptions = this.reinitOptions[breakpointValue];
        } else {
            const nextBreakpointIndex = this.reinitBreakpoints.findIndex(value => value === breakpoint) + 1;

            if (nextBreakpointIndex < this.reinitBreakpoints.length) {
                const actualBreakpoint = this.reinitBreakpoints[nextBreakpointIndex];

                breakpointValue = actualBreakpoint.media.match(rMediaMatch)[1];
                newOptions = this.reinitOptions[breakpointValue];
            } else {
                newOptions = this.reinitOptions.default;
            }
        }

        Object.assign(this.options, newOptions);

        /* Прежде чем удалять карусель, необходимо убрать ссылку на неё у синхронизированной карусели. */
        if (this.controlCarousel) {
            this.controlCarousel.instance.controller.control = undefined;
        }

        const curIndex = this.instance.realIndex;

        this.instance.destroy();
        this.instance = new Swiper(this.$block, this.options);

        /* Возвращаем карусель в состояние, которое было на момент пересоздания. */
        this.instance.slideTo(curIndex);

        /* После пересоздания возвращаем ссылку на instance у связанной карусели. */
        if (this.controlCarousel) {
            this.controlCarousel.instance.controller.control = this.instance;
        }
    }

    /**
     * Выполняет связку каруселей при использовании модуля Controller.
     *
     * @param {Carousel} Контролируемая карусель.
     */
    initController(controlCarousel) {
        /* Сохраняем ссылку на объект связанной карусели для удобства доступа в будущем. */
        this.controlCarousel = controlCarousel;
        /* В options сохраняем полноценный объект controller. */
        this.options.controller = {
            control: controlCarousel.instance,
        };
        /* А в текущих настройках только переназначаем control, чтобы не сбить дефолтные значения остальных опций объекта controller. */
        this.instance.controller.control = controlCarousel.instance;
    }
}

$(document).on('click', '.carousel__filters-btn', handleFiltersBtnClick);

function handleFiltersBtnClick(e) {
    const { currentTarget } = e;
    const $btn = $(currentTarget);
    if ($btn.hasClass('carousel__filters-btn--active')) return;

    const $block = $btn.closest('.carousel');
    const carousel = Carousel.getInstance($block);
    const $slides = $('.swiper-slide', $block);
    const $activeBtn = $('.carousel__filters-btn--active', $block);
    const filterType = $btn.attr('data-filter');

    $slides.removeClass('swiper-slide--hidden');

    if (filterType !== 'all') {
        $slides.not(`[data-filter=${filterType}]`).addClass('swiper-slide--hidden');
    }

    $activeBtn.removeClass('carousel__filters-btn--active');
    $btn.addClass('carousel__filters-btn--active');

    carousel.instance.update();
}

/**
 * Исправление бага, связанного с фокусом элемента
 * При фокусе слайдер съезжал
 */

let preventFocusSwipe = false;
$(document).on('focus', '.carousel .swiper-slide', e => {
    if (preventFocusSwipe) return;

    const $slide = $(e.currentTarget).closest('.swiper-slide');
    const $carousel = $slide.closest('.carousel');
    const $carouselContainer = $slide.closest('.swiper-container');
    const currentCarouselInstance = Carousel.getInstance($carousel).instance;
    const visibleSlidesIndexes = [];

    if ($carousel.children().length >= 1) return;

    $carouselContainer[0].scrollLeft = 0;

    $carousel.find('.swiper-slide').each((i, item) => {
        visibleSlidesIndexes.push($(item).index());
    });

    const index = visibleSlidesIndexes.findIndex(elem => {
        return elem === $slide.index();
    });

    currentCarouselInstance.slideTo(index);
});

// Предотвращение действия фокуса при клике
$(document).on('mousedown', '.carousel .swiper-slide', () => {
    preventFocusSwipe = true;

    setTimeout(() => {
        preventFocusSwipe = false;
    }, 100);
});
